import {
    ModuleAIReviewFinding,
    ModuleAIReviewSection,
} from 'src/services/backend/ModuleAIReviewService'

/** A finding with the 1-based module page it belongs to; undefined when it is module-wide. */
export type ResolvedAIReviewFinding = ModuleAIReviewFinding & { pageNumber?: number }

export type ResolvedAIReviewSection = Omit<ModuleAIReviewSection, 'findings'> & {
    findings: ResolvedAIReviewFinding[]
}

const WORKFLOW_PAGE_PATH = /^workflow\[(\d+)\]/

/**
 * Maps every `id` and `label` found anywhere inside a page to that page's 1-based number, so a
 * finding anchored only by entityId (as model findings often are) can still be placed on a page.
 * The first page to claim a value wins.
 */
export function buildEntityPageIndex(workflow: readonly unknown[]): Map<string, number> {
    const index = new Map<string, number>()
    const walk = (value: unknown, pageNumber: number) => {
        if (Array.isArray(value)) {
            value.forEach((child) => walk(child, pageNumber))
            return
        }
        if (!value || typeof value !== 'object') {
            return
        }
        for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
            if ((key === 'id' || key === 'label') && typeof child === 'string' && child) {
                if (!index.has(child)) {
                    index.set(child, pageNumber)
                }
            } else {
                walk(child, pageNumber)
            }
        }
    }
    workflow.forEach((page, i) => walk(page, i + 1))
    return index
}

/**
 * Resolves the page a finding belongs to. The attributePath is authoritative when it starts with
 * `workflow[N]` (which is how the structural checks anchor findings); otherwise the entityId is
 * looked up among the ids and labels of each page.
 */
export function resolveFindingPageNumber(
    finding: ModuleAIReviewFinding,
    entityPageIndex: Map<string, number>,
    pageCount: number
): number | undefined {
    const match = finding.attributePath ? WORKFLOW_PAGE_PATH.exec(finding.attributePath) : null
    if (match) {
        const pageNumber = Number(match[1]) + 1
        if (pageNumber <= pageCount) {
            return pageNumber
        }
    }
    return finding.entityId ? entityPageIndex.get(finding.entityId) : undefined
}

export function resolveSectionPages(
    sections: readonly ModuleAIReviewSection[],
    workflow: readonly unknown[]
): ResolvedAIReviewSection[] {
    const entityPageIndex = buildEntityPageIndex(workflow)
    return sections.map((section) => ({
        ...section,
        findings: (section.findings ?? []).map((finding) => ({
            ...finding,
            pageNumber: resolveFindingPageNumber(finding, entityPageIndex, workflow.length),
        })),
    }))
}

/** Sections narrowed to the findings on one page, dropping sections left empty. */
export function sectionsForPage(
    sections: readonly ResolvedAIReviewSection[],
    pageNumber: number
): ResolvedAIReviewSection[] {
    return sections
        .map((section) => ({
            ...section,
            findings: section.findings.filter((f) => f.pageNumber === pageNumber),
        }))
        .filter((section) => section.findings.length > 0)
}
